<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrency } from '@composables/useCurrency';
import Panel from '../../../../_02_molecules/components/Panel.vue';
import UISelect from '../../../../_01_atoms/components/UISelect.vue';
import { trans } from '../../../../_00_elements/translation';
import useSubscriptionStore from '../../store/subscriptions';
import { displayCurrencyWithoutCurrencyCode } from '../../../../_00_elements/number-formatter';

const { currencyEntity } = useCurrency();

const subscriptionStore = useSubscriptionStore();
const emit = defineEmits([ 'next-step' ]);

const { invoice } = storeToRefs(subscriptionStore);
const amountError = ref(null);

const billable = currencyEntity(invoice);

const validateAmount = () => {
  amountError.value = billable.amount > 0 ? null : trans('gestion.subscription.step.4.amount.error');
};

const isNextDisabled = computed(() => !billable.amount || amountError.value);

const updateTaxAmount = () => {
  billable.taxAmount = billable.amount * (billable.vatRate / 100);
  billable.totalAmount = billable.amount + billable.taxAmount;
};

const formatCurrency = (value) => {
  if (Number.isNaN(value)) {
    return '';
  }

  return displayCurrencyWithoutCurrencyCode(value);
};

const formattedTaxAmount = computed(() => formatCurrency(billable.taxAmount));
const formattedTotalAmount = computed(() => formatCurrency(billable.totalAmount));

const onClickNext = () => {
  validateAmount();

  if (!amountError.value) {
    emit('next-step');
  }
};
</script>

<template>
  <div class="stepper-subscription amount-subscription">
    <h3 class="stepper-subscription__title">
      {{ trans('gestion.subscription.step.4.title') }}
    </h3>
    <panel type="info" :content="trans('gestion.subscription.step.4.panel')" icon="ph_lightbulb-fill_blue" />
    <div class="amount-subscription__form">
      <label for="subscriptionFormAmount" class="infos-subscription__label">
        {{ trans('gestion.subscription.step.4.amount') }}
      </label>
      <div class="input-icon">
        <input
          id="subscriptionFormAmount"
          v-model="billable.amount"
          inputmode="decimal"
          class="amount-subscription__input"
          :class="{ error: amountError }"
          type="number"
          @blur="validateAmount"
          @input="updateTaxAmount"
        >
        <i>€ HT</i>
      </div>
      <span v-if="amountError" class="error-message">{{ amountError }}</span>
    </div>
    <div class="amount-subscription__form">
      <label for="subscriptionFormVatRate" class="infos-subscription__label">
        {{ trans('gestion.subscription.step.4.vat') }}
      </label>
      <UISelect
        v-model="billable.vatRate"
        :options="[ { label: '0%', value: 0 }, { label: '20%', value: 20 } ]"
        class="amount-subscription__select"
        @change="updateTaxAmount"
      />
    </div>
    <div class="amount-subscription__form">
      <label for="subscriptionFormTaxAmount" class="infos-subscription__label">
        {{ trans('gestion.subscription.step.4.tax_amount') }}
      </label>
      <div class="input-icon">
        <input
          id="subscriptionFormTaxAmount"
          :value="formattedTaxAmount"
          class="amount-subscription__input"
          type="text"
          disabled="disabled"
        >
        <i>€</i>
      </div>
    </div>
    <div class="amount-subscription__form">
      <label for="subscriptionFormTotalAmount" class="infos-subscription__label">
        {{ trans('gestion.subscription.step.4.total_amount') }}
      </label>
      <div class="input-icon">
        <input
          id="subscriptionFormTotalAmount"
          :value="formattedTotalAmount"
          class="amount-subscription__input"
          type="text"
          disabled="disabled"
        >
        <i>€ TTC</i>
      </div>
    </div>
    <div class="stepper-subscription__mandatory-infos">
      {{ trans('gestion.subscription.general.mandatory_fields') }}
    </div>
    <button
      class="btn btn--primary stepper-subscription__button"
      :disabled="isNextDisabled"
      @click="onClickNext"
    >
      {{ trans('global.pagination.next') }}
    </button>
  </div>
</template>
